/*
  KitchenBuildr mobile drawer: Components category tabs.

  Tabs are anchors, not buttons, on purpose. Dawn's MenuDrawer binds a
  "close submenu" handler to every <button> inside the drawer, so a button tab
  would slide the panel shut. Anchors also give a no-JS fallback: they link to
  the category collection.
*/
if (!customElements.get('kb-drawer-components')) {
  customElements.define(
    'kb-drawer-components',
    class KbDrawerComponents extends HTMLElement {
      connectedCallback() {
        if (this.initialised) return;
        this.initialised = true;

        this.details = this.querySelector('[data-kb-components-details]');
        if (!this.details) return;

        this.summary = this.details.querySelector('summary');
        this.scroller = this.details.querySelector('.menu-drawer__inner-submenu');
        this.tabList = this.querySelector('[data-kb-tablist]');
        this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
        this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

        const selected = this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
        this.current = selected > -1 ? selected : 0;

        this.tabs.forEach((tab, index) => {
          tab.addEventListener('click', (event) => {
            event.preventDefault();
            this.select(index);
          });
          tab.addEventListener('keydown', (event) => this.onTabKeydown(event, index));
        });

        // Category tiles on the first drawer screen open the panel on that category.
        this.querySelectorAll('[data-kb-open-category]').forEach((tile) => {
          tile.addEventListener('click', (event) => {
            if (this.details.hasAttribute('open')) return;
            event.preventDefault();
            this.select(Number(tile.dataset.kbOpenCategory), { instant: true, resetScroll: true });
            this.summary.click();
          });
        });

        // Fade the left edge once chips have scrolled under the back button.
        if (this.tabList) {
          const updateEdge = () => this.tabList.classList.toggle('is-scrolled', this.tabList.scrollLeft > 4);
          this.tabList.addEventListener('scroll', updateEdge, { passive: true });
        }

        // Chips can only be measured once the panel is rendered.
        this.details.addEventListener('toggle', () => {
          if (this.details.open) this.centerTab(this.current, true);
        });
      }

      select(index, { focus = false, instant = false, resetScroll = false } = {}) {
        if (Number.isNaN(index) || index < 0 || index >= this.tabs.length) return;

        const changed = index !== this.current;

        this.tabs.forEach((tab, i) => {
          const active = i === index;
          tab.classList.toggle('is-active', active);
          tab.setAttribute('aria-selected', active ? 'true' : 'false');
          tab.tabIndex = active ? 0 : -1;
        });

        this.panels.forEach((panel, i) => {
          const active = i === index;
          panel.hidden = !active;
          panel.classList.toggle('is-active', active);
        });

        this.current = index;

        if ((changed || resetScroll) && this.scroller) this.scroller.scrollTop = 0;
        if (focus) this.tabs[index].focus({ preventScroll: true });
        this.centerTab(index, instant);
      }

      centerTab(index, instant = false) {
        const tab = this.tabs[index];
        const list = this.tabList;
        if (!tab || !list || !list.clientWidth) return;

        const left = tab.offsetLeft - (list.clientWidth - tab.offsetWidth) / 2;
        const behavior = instant || this.reducedMotion.matches ? 'auto' : 'smooth';

        list.scrollTo({ left: Math.max(0, left), behavior });
      }

      onTabKeydown(event, index) {
        const last = this.tabs.length - 1;
        let next = null;

        switch (event.key) {
          case 'ArrowRight':
            next = index === last ? 0 : index + 1;
            break;
          case 'ArrowLeft':
            next = index === 0 ? last : index - 1;
            break;
          case 'Home':
            next = 0;
            break;
          case 'End':
            next = last;
            break;
          case ' ':
          case 'Enter':
            event.preventDefault();
            this.select(index);
            return;
          default:
            return;
        }

        event.preventDefault();
        this.select(next, { focus: true });
      }
    }
  );
}
